import { DrillFooterContainer } from "@-/dev.improve.haniverse.routes.nav.app._components.drill.footer";
import { DrillPageContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { DrillPageFooter } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.footer";
import { Portal } from "@-/com.npmjs.react-native-teleport";
import {
  LearnDeckContext,
  useLearnCardSurface,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { DrillPageRevealTopLoader } from "#drill-page-reveal-top-loader";
import { isSentenceProductionDrillKind } from "@-/dev.improve.haniverse.lib.drill.entities";
import {
  useIsRevealing,
  useRevealMutation,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.reveal";
import { useDrillPageAnswerMode } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.answer-mode";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useRef, type ReactNode } from "react";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";

/**
 * The drill front's prompt and tap-to-reveal target, with a separate stroke replay button.
 * Free-text answer inputs render through a
 * portal in the stationary action area below the card, preserving this surface's live contexts.
 * The pronunciation footer portals its input separately, keeping its keyboard on the card.
 * Sentence controls remain with their prompts. The deck's horizontal
 * pan owns ratings; this Pressable reveals the card as soon as a finger lands, without waiting
 * for the release.
 */
export function DrillPageContainer({
  children,
  headerCenter,
  headerEnd,
}: {
  children: ReactNode;
  headerCenter?: ReactNode;
  headerEnd?: ReactNode;
}) {
  const { isActive, isCurrent, presentationKey = "answer-input" } = useLearnCardSurface();
  const [sessionId] = useContextSelectors(
    LearnDeckContext,
    (c) => c.state.current.presentation.sessionId,
  );
  const revealMutation = useRevealMutation();
  const isPressedInRef = useRef(false);
  const isRevealing = useIsRevealing();
  const { isAnswerBarHidden, reviewMode } = useDrillPageAnswerMode();
  const [
    drillType,
    isShuangpinKeyboardToggleHidden,
    isShuangpinKeyboardVisible,
    onToggleShuangpinKeyboard,
    setGlyphReplayKey,
  ] = useContextSelectors(
    DrillPageContext,
    (c) => c.drillType,
    (c) => c.isShuangpinKeyboardToggleHidden,
    (c) => c.isShuangpinKeyboardVisible,
    (c) => c.onToggleShuangpinKeyboard,
    (c) => c.setGlyphReplayKey,
  );
  const isMeaningReview = drillType.kind === "meaning";
  const [displayMode] = useContextSelectors(NavContext, (c) => c.characterDisplayMode);
  const isPronunciationReview = drillType.kind === "pronunciation";
  const isGlyphReview = isMeaningReview || isPronunciationReview;
  const hasExternalAnswerInput = drillType.kind === "meaning" || drillType.kind === "word-sentence";
  // A sentence drill is answered incrementally, and reveal ENDS it — so a full-face tap target
  // would let one stray touch abandon a sentence the user was midway through. Reveal stays
  // available, but only from the explicit footer control.
  const isSentenceProductionReview = isSentenceProductionDrillKind(drillType.kind);

  return (
    <View className="relative flex-1 overflow-hidden bg-surface" testID="drill-card-front">
      <Pressable
        accessibilityLabel="Reveal card"
        accessibilityRole="button"
        className="absolute inset-0 z-0"
        disabled={!isActive || revealMutation.isPending || isSentenceProductionReview}
        onPress={() => {
          // A touch already revealed at its press-in. Only an activation without one (TalkBack, a
          // web screen reader's click) reveals here.
          const wasPressedIn = isPressedInRef.current;
          isPressedInRef.current = false;
          if (!wasPressedIn) {
            logger(import.meta.location).info("reveal:onPress", { from: "bg-pressable" });
            revealMutation.mutate();
          }
        }}
        onPressIn={() => {
          isPressedInRef.current = true;
          logger(import.meta.location).info("reveal:onPressIn", { from: "bg-pressable" });
          revealMutation.mutate();
        }}
        testID="drill-reveal-pressable"
      />

      <View
        // The bottom inset keeps the glyph reviews' "Tap to reveal" hint clear of the content;
        // an open pinyin keyboard replaces that hint at the bottom of the card.
        className={`min-h-0 flex-1 flex-col ${isPronunciationReview && isShuangpinKeyboardVisible ? "pt-10" : isGlyphReview ? "pt-10 pb-14" : "pt-8 pb-14"}`}
        pointerEvents="box-none"
      >
        <View className="min-h-0 flex-1" pointerEvents="box-none">
          {children}
        </View>
        {hasExternalAnswerInput ? null : (
          <DrillFooterContainer
            className={isPronunciationReview ? "shrink-0" : undefined}
            isKeyboardSticky={false}
          >
            {reviewMode === "answer" && !isAnswerBarHidden ? <DrillPageFooter /> : null}
          </DrillFooterContainer>
        )}
      </View>
      {isGlyphReview && displayMode === "svg" ? (
        <Pressable
          accessibilityLabel="Replay stroke animation"
          accessibilityRole="button"
          className="absolute top-2.25 left-2.25 size-6 items-center justify-center rounded-full active:bg-accent"
          disabled={!isActive || revealMutation.isPending}
          hitSlop={10}
          onPress={() => setGlyphReplayKey((key) => key + 1)}
          testID="drill-replay-strokes"
        >
          <Icon name="LuRotateCcw" size={24} />
        </Pressable>
      ) : null}
      {/* The collapsed pinyin keyboard's toggle mirrors the replay button in the bottom-left
          corner, beside "Tap to reveal". Once expanded, the keyboard's own bottom-left key
          collapses it. */}
      {isPronunciationReview &&
      reviewMode === "answer" &&
      !isAnswerBarHidden &&
      !isShuangpinKeyboardVisible &&
      !isShuangpinKeyboardToggleHidden ? (
        <Pressable
          accessibilityLabel="Show pinyin keyboard"
          accessibilityRole="button"
          accessibilityState={{ expanded: false }}
          className="absolute bottom-2.25 left-2.25 size-6 items-center justify-center rounded-full active:bg-accent"
          disabled={!isActive || revealMutation.isPending}
          hitSlop={10}
          onPress={onToggleShuangpinKeyboard}
          testID="pronunciation-keyboard-toggle"
        >
          <Icon name="MsLanguageChinesePinyin" size={24} />
        </Pressable>
      ) : null}
      {headerCenter ? (
        <View className="absolute inset-x-12 top-2.25 h-6 justify-center" pointerEvents="none">
          {headerCenter}
        </View>
      ) : null}
      {headerEnd}
      <DrillPageRevealTopLoader active={isRevealing} />
      {hasExternalAnswerInput && isCurrent && reviewMode === "answer" && !isAnswerBarHidden ? (
        <Portal
          className="size-full"
          hostName={`learn-card-answer-input-${sessionId}`}
          name={presentationKey ?? "answer-input"}
        >
          <DrillPageFooter />
        </Portal>
      ) : null}
    </View>
  );
}
